import React from 'react';
import { appWithTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import { ThemeProvider as NextThemesProvider } from 'next-themes';
import { StylesProvider } from '@mui/styles';
import '@epic-typeface/inter';
import { getEpicI18nContext } from '@epic-mw/next-configure';

import Script from 'next/script';
import { useAccountStore } from '@client/api/use-account-store';
import { usePublicRuntimeSettings } from '@client/api/use-public-settings';
import { UserProvider } from '@contexts/User';
import { UrlHashProvider } from '@contexts/UrlHash';
import { LocaleProvider } from '@contexts/Locale';
import { AppHead } from '@shared/AppHead/AppHead';
import { DEFAULT_LOCALE } from '@epic-mw/localization';
import type { LocalesUnion } from '@constants/i18n';
// eslint-disable-next-line import/extensions
import nextI18nConfig from '../../next-i18next.config.js';

function DocsPortalApp({ Component, pageProps }: AppProps) {
  const locale = (pageProps.locale || DEFAULT_LOCALE) as LocalesUnion;

  const { isLoading, publicRuntimeSettings } = usePublicRuntimeSettings();
  const i18nContext = getEpicI18nContext(pageProps, locale);
  const { account } = useAccountStore();

  const navigationUrl = `${publicRuntimeSettings.marketingUrl}/navigation/eos-navigation.mjs`;
  const footerUrl = `${publicRuntimeSettings.marketingUrl}/footer/epicgames-footer.mjs`;

  return (
    !isLoading && (
      <StylesProvider>
        <Script src="https://tracking.epicgames.com/tracking.js" strategy="lazyOnload" />
        <Script src={navigationUrl} type="module" crossOrigin="anonymous" />
        <Script src={footerUrl} type="module" crossOrigin="anonymous" />
        <NextThemesProvider storageKey="eosDocsTheme" defaultTheme="system" enableSystem enableColorScheme={false}>
          <LocaleProvider locale={locale}>
            <UserProvider account={account}>
              <UrlHashProvider>
                <AppHead />
                <Component {...pageProps} i18nContext={i18nContext} publicRuntimeSettings={publicRuntimeSettings} />
              </UrlHashProvider>
            </UserProvider>
          </LocaleProvider>
        </NextThemesProvider>
      </StylesProvider>
    )
  );
}

export default appWithTranslation(DocsPortalApp, nextI18nConfig);
